// frontend/src/hooks/useTalentInsightsAggregate.js
import { useQueries } from '@tanstack/react-query';
import { talentAssessmentsAPI } from '../services/api';

const FIVE_MINUTES = 5 * 60 * 1000;

const retryFn = (failureCount, error) => {
  const status = error?.response?.status;
  if (status === 403 || status === 404) return false;
  return failureCount < 2;
};

const fetchAggregate = async (segmentType) => {
  const { data } = await talentAssessmentsAPI.adminGetBigFiveAggregate({
    segment_type: segmentType,
    dimension: 'trait',
  });
  return data;
};

/**
 * Talent Insights — Big Five aggregate data for the Admin dashboard.
 *
 * The company benchmark is ALWAYS fetched, regardless of which segment
 * the HR user is currently viewing:
 *   - It is the primary payload in "Company-wide" mode.
 *   - It doubles as the always-on baseline series overlaid on the
 *     department comparison radar, so switching to "Department" never
 *     shows a blank baseline while a second request is in flight.
 *
 * The department benchmark is fetched only when the user is looking at
 * Department mode (`segmentType === 'department'`), matching the
 * previous single-fetch-per-mode behaviour for that request.
 *
 * @param {'company'|'department'} segmentType
 */
export const useTalentInsightsAggregate = (segmentType) => {
  const [companyQuery, departmentQuery] = useQueries({
    queries: [
      {
        queryKey: ['talentInsights', 'aggregate', 'company'],
        queryFn: () => fetchAggregate('company'),
        staleTime: FIVE_MINUTES,
        retry: retryFn,
      },
      {
        queryKey: ['talentInsights', 'aggregate', 'department'],
        queryFn: () => fetchAggregate('department'),
        staleTime: FIVE_MINUTES,
        retry: retryFn,
        enabled: segmentType === 'department',
      },
    ],
  });

  const activeQuery = segmentType === 'department' ? departmentQuery : companyQuery;

  return {
    companyData: companyQuery.data,
    departmentData: departmentQuery.data,
    data: activeQuery.data,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    isError: activeQuery.isError,
  };
};
